import { z } from 'zod'
import { isoDate, isoDateTime, uuid } from './common.js'

/**
 * Travel document details — fields, never images. Build unit 7.16.
 *
 * Ana needs six fields to ticket a trip: name exactly as printed, document
 * number, nationality, date of birth, expiry, issuing country. A passport,
 * ID card or visa photo is only how those fields travel — it is never
 * written anywhere. There is no field in this module, and no column on
 * `travel_documents`, capable of holding bytes or a storage key. See
 * PATTERNS.md §6 and the build spec §5 / §6 invariant 9 / §9 for the
 * reasoning; `travelDocuments.test.ts` asserts the absence at the schema
 * level, not just here.
 *
 * Advisor-only throughout: `itin_share` holds no grant of any kind on
 * `travel_documents` (see `0008_phase2_rls.sql`). A traveller never reads
 * their own document details back through a share link, so this contract —
 * unlike `participants` — has no "identity" / "for share" variant.
 */

export const travelDocumentKind = z.enum(['passport', 'id_card', 'visa'])
export type TravelDocumentKind = z.infer<typeof travelDocumentKind>

/** ISO 3166-1 alpha-2, upper case — 'BR', 'US', never the three-letter form. */
export const countryCode = z
  .string()
  .length(2)
  .regex(/^[A-Z]{2}$/, 'expected an ISO 3166-1 alpha-2 code such as BR')

const travelDocumentWritableFields = {
  /**
   * Airlines match this character for character against the document. It is
   * deliberately a separate field from the participant's `displayName` —
   * legal names legitimately differ from what a traveller is called — and a
   * mismatch is surfaced as a warning, never blocked.
   */
  fullNameAsPrinted: z.string().trim().min(1).max(200),
  documentNumber: z.string().trim().min(1).max(64),
  nationality: countryCode.nullish(),
  issuingCountry: countryCode.nullish(),
  dateOfBirth: isoDate.nullish(),
  /** Most destinations want six months' validity beyond the return date. */
  expiresOn: isoDate.nullish(),
}

export const createTravelDocumentRequest = z.object({
  kind: travelDocumentKind.default('passport'),
  ...travelDocumentWritableFields,
  /**
   * NOT NULL at the database (spec §9): a document number plus a name plus a
   * date of birth is identity-theft material even without the picture, so
   * retention is mandatory rather than optional. Omit to default to trip end
   * + 6 months; an advisor may still shorten or extend it explicitly.
   */
  purgeAfter: isoDateTime.nullish(),
})
export type CreateTravelDocumentRequest = z.infer<typeof createTravelDocumentRequest>

/** `kind` is absent on purpose — changing it would change which unique "slot" a document occupies. */
export const updateTravelDocumentRequest = z
  .object({ ...travelDocumentWritableFields, purgeAfter: isoDateTime })
  .partial()
export type UpdateTravelDocumentRequest = z.infer<typeof updateTravelDocumentRequest>

/**
 * Surfaced as warnings, never as hard errors (spec §7.16) — both checks below
 * describe conditions that are worth flagging, not conditions that make the
 * record invalid. Ana must still be able to save what she has.
 */
export const travelDocumentWarningCode = z.enum([
  /** `expiresOn` falls before the trip's return date — caught at check-in otherwise. */
  'expired_before_return',
  /** `expiresOn` falls short of six months past the return date. */
  'expires_within_six_months',
  /** `fullNameAsPrinted` does not match the participant's `displayName`. */
  'name_mismatch',
])
export type TravelDocumentWarningCode = z.infer<typeof travelDocumentWarningCode>

export const travelDocumentWarning = z.object({
  code: travelDocumentWarningCode,
  /** Values only — the message text lives in the i18n layer, keyed by `code`. */
  params: z.record(z.string(), z.string()).optional(),
})
export type TravelDocumentWarning = z.infer<typeof travelDocumentWarning>

export const travelDocument = z.object({
  id: uuid,
  tripId: uuid,
  participantId: uuid,
  kind: travelDocumentKind,
  fullNameAsPrinted: z.string(),
  documentNumber: z.string(),
  nationality: countryCode.nullable(),
  issuingCountry: countryCode.nullable(),
  dateOfBirth: isoDate.nullable(),
  expiresOn: isoDate.nullable(),
  purgeAfter: isoDateTime,
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
  /** Computed at read time from the trip's return date and the participant's name. Never stored. */
  warnings: z.array(travelDocumentWarning),
})
export type TravelDocument = z.infer<typeof travelDocument>
